import CookieHelper from './cookie-helper';

interface LocationData {
  name: string;
  location: {
    formatted?: string | null;
    lat: number | null;
    lng: number | null;
    state: string;
    zip: string;
    city: string;
    country: string;
  };
  isCurrentLocation: boolean;
}

class GeolocationHelper {
  static setLocation() {
    const cookie = CookieHelper.getCookie('nuvance-preferred-location') || '{}';
    try {
      // Validate that the cookie contains a valid LocationData object
      const cookieData = JSON.parse(cookie);
      if (
        cookieData &&
        typeof cookieData === 'object' &&
        'name' in cookieData &&
        'location' in cookieData
      ) {
        cookieData.isCurrentLocation = true;
        CookieHelper.setCookie(
          'nuvance-preferred-location',
          JSON.stringify(cookieData),
          30
        );
      } else {
        console.warn('Cookie does not contain valid location data');
      }
    } catch (error) {
      console.error('Error parsing cookie:', error);
    }
  }

  static async useCurrentLocation(locationInput: HTMLInputElement | null) {
    try {
      const position = await this.getCurrentPosition();
      const { latitude, longitude } = position.coords;
      const location = await this.fetchLocationFromCoords(latitude, longitude);
      if (
        location &&
        location.city &&
        location.state &&
        location.zip &&
        location.country
      ) {
        const cookieData: LocationData = {
          name: `${location.city}, ${location.state} ${location.zip}`,
          location: {
            lat: latitude,
            lng: longitude,
            state: location.state,
            zip: location.zip,
            city: location.city,
            country: location.country,
          },
          isCurrentLocation: false,
        };
        CookieHelper.setCookie(
          'nuvance-preferred-location',
          JSON.stringify(cookieData),
          30
        );
        if (locationInput) {
          locationInput.value = cookieData.name;
        }
      }
    } catch (error) {
      console.error('Error fetching current location:', error);
    }
  }

  static getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
      } else {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      }
    });
  }

  static async fetchLocationFromCoords(
    latitude: number,
    longitude: number
  ): Promise<{ city: string; state: string; zip: string; country: string }> {
    const baseUrl = import.meta.env.VITE_DRUPAL_URL_LOCAL ?? '';
    const myHeaders = new Headers();
    myHeaders.append('X-Custom-Header', 'X-Nuvance-Location-Get');
    const response = await fetch(
      `${baseUrl}/geolocation?lat=${latitude}&lon=${longitude}`,
      {
        method: 'GET',
        headers: myHeaders,
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch location data');
    }
    const data = await response.json();
    if (data.status !== 'OK' || !data.results || data.results.length === 0) {
      throw new Error('Failed to fetch location data');
    }
    const result = data.results[0];
    const location = {
      city:
        result.address_components.find(
          (component: { types: string[]; long_name: string }) =>
            component.types.includes('locality')
        )?.long_name || '',
      state:
        result.address_components.find(
          (component: { types: string[]; short_name: string }) =>
            component.types.includes('administrative_area_level_1')
        )?.short_name || '',
      zip:
        result.address_components.find(
          (component: { types: string[]; long_name: string }) =>
            component.types.includes('postal_code')
        )?.long_name || '',
      country:
        result.address_components.find(
          (component: { types: string[]; short_name: string }) =>
            component.types.includes('country')
        )?.short_name || '',
    };
    return location;
  }

  static async getLatLonFromAddress(address: string): Promise<{
    latitude: number;
    longitude: number;
    formatted_address: string;
  }> {
    const baseUrl = import.meta.env.VITE_DRUPAL_URL_LOCAL ?? '';
    const myHeaders = new Headers();
    myHeaders.append('X-Custom-Header', 'X-Nuvance-Location-Get');
    const response = await fetch(
      `${baseUrl}/geolocation_latlon?address=${encodeURIComponent(address)}`,
      {
        method: 'GET',
        headers: myHeaders,
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch latitude and longitude');
    }
    const data = await response.json();
    if (!data.latitude || !data.longitude) {
      throw new Error('Invalid response for latitude and longitude');
    }
    return {
      latitude: data.latitude,
      longitude: data.longitude,
      formatted_address: data.formatted_address,
    };
  }

  static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 3958.8; // Radius of Earth in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Builds a Google Maps URL for an address with optional coordinates
   * @param formattedAddress The formatted address for the map link
   * @param coordinates Optional latitude and longitude coordinates
   * @returns A Google Maps URL
   */
  static buildGoogleMapsUrl(
    formattedAddress: string,
    coordinates?: { latitude: number; longitude: number }
  ): string {
    const coordsParam =
      coordinates?.latitude && coordinates?.longitude
        ? `&map_action=map&center=${coordinates.latitude},${coordinates.longitude}`
        : '';

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}${coordsParam}`;
  }

  /**
   * Calculate distance between a given address and the user's preferred location
   * @param address The address to calculate distance to
   * @returns Object containing distance, formatted address and coordinates, or null if calculation failed
   */
  static async calculateDistanceForAddress(address: string): Promise<{
    distance: number;
    formattedAddress: string;
    coordinates: { latitude: number; longitude: number };
  } | null> {
    if (!address) {
      return null;
    }

    const cookie = CookieHelper.getCookie('nuvance-preferred-location');
    if (!cookie) {
      return null;
    }

    try {
      const preferredLocation = JSON.parse(cookie);
      if (
        !preferredLocation?.location?.lat ||
        !preferredLocation?.location?.lng
      ) {
        return null;
      }

      const { latitude, longitude, formatted_address } =
        await GeolocationHelper.getLatLonFromAddress(address);

      if (latitude && longitude) {
        const distance = GeolocationHelper.calculateDistance(
          preferredLocation.location.lat,
          preferredLocation.location.lng,
          latitude,
          longitude
        );

        return {
          distance,
          formattedAddress: formatted_address,
          coordinates: { latitude, longitude },
        };
      }
    } catch (error) {
      console.error('Error calculating distance for address:', error);
    }

    return null;
  }

  /**
   * Calculate distance between given coordinates and the user's preferred location
   * @param latitude The latitude of the location
   * @param longitude The longitude of the location
   * @returns Object containing distance and coordinates, or null if calculation failed
   */
  static calculateDistanceForCoordinates(
    latitude: number,
    longitude: number
  ): {
    distance: number;
    coordinates: { latitude: number; longitude: number };
  } | null {
    const cookie = CookieHelper.getCookie('nuvance-preferred-location');
    if (!cookie) {
      return null;
    }

    try {
      const preferredLocation = JSON.parse(cookie);
      if (
        !preferredLocation?.location?.lat ||
        !preferredLocation?.location?.lng
      ) {
        return null;
      }

      const distance = GeolocationHelper.calculateDistance(
        preferredLocation.location.lat,
        preferredLocation.location.lng,
        latitude,
        longitude
      );

      return {
        distance,
        coordinates: { latitude, longitude },
      };
    } catch (error) {
      console.error('Error calculating distance for coordinates:', error);
    }

    return null;
  }
}

export default GeolocationHelper;
